import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackAppClosed } from "@/infra/observability/analyticsService";
import { QueryKeys } from "@/infra/query";
import type { Patient } from "@akina/domain";
import type { Treatment } from "types/schemas";
import { addPatientAndTreatmentData, useTrackCareEventInfo } from "@akina/infra";
import { isApp } from "@akina/utils";
import { useQueryClient } from "@tanstack/react-query";
import { getCurrentWindow } from "@tauri-apps/api/window";

export const useTrackAppClosed = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getData: getCareData } = useTrackCareEventInfo({
    pageName: location.pathname,
  });

  // Keep refs for stable access in cleanup
  const getCareDataRef = useRef(getCareData);
  useEffect(() => {
    getCareDataRef.current = getCareData;
  }, [getCareData]);

  const getAnalyticsData = useCallback(() => {
    const patient = queryClient.getQueryData<Patient>([QueryKeys.fetchPatient]);
    const activeTreatment = queryClient.getQueryData<Treatment>([QueryKeys.fetchActiveTreatment]);

    if (!patient || !activeTreatment) {
      console.warn("[Analytics] Patient/Treatment data not cached at tracking time - event will have empty user data");
    }

    const careData = getCareDataRef.current();
    return addPatientAndTreatmentData(patient, activeTreatment, careData);
  }, [queryClient]);

  useEffect(() => {
    if (!isApp) return;
    if (isExternal(location.pathname)) return;

    const unlisten = getCurrentWindow().onCloseRequested(async event => {
      event.preventDefault();
      try {
        // Compute analytics data lazily at tracking time
        const analyticsData = getAnalyticsData();
        // fire and forget, do not await
        trackAppClosed(analyticsData);
      } finally {
        getCurrentWindow().destroy();
      }
    });

    return () => {
      unlisten.then(unlisten => unlisten());
    };
  }, [location.pathname, getAnalyticsData]);
};

function isExternal(pathname: string) {
  return pathname.includes("login");
}
